import * as React from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import type { ActivityStatus } from '@user/features/Activity/constants'
import type { CalendarEvent } from '../../typings'
import { StatusChip } from './StatusChip'

type Props = {
  event: CalendarEvent
  isMarker: boolean
  title: string
  chipLabel: string
  /** The solid source colour for the header chip. */
  chipColor: string
  /** Present only when the status is BOTH renderable and writable — see the
   *  gate in EventDetailPopover. Absent hides the chip entirely (Q3). */
  editableStatus?: { activityId: number; value: ActivityStatus }
  onStatusCommitted?: (status: ActivityStatus) => void
}

/** The popover header: source chip, title, activity type line, and status chip. */
export function PopoverHead({
  event,
  isMarker,
  title,
  chipLabel,
  chipColor,
  editableStatus,
  onStatusCommitted,
}: Props) {
  const theme = useTheme()

  return (
    <Box sx={{ padding: '0 24px 16px' }}>
      <Chip
        label={chipLabel}
        size="small"
        sx={{
          height: 24,
          fontWeight: 600,
          color: theme.palette.common.white,
          backgroundColor: chipColor,
        }}
      />
      <Typography
        component="h3"
        sx={{
          mt: 1,
          fontFamily: theme.typography.h6.fontFamily,
          fontSize: '18px',
          fontWeight: 600,
          color: theme.palette.text.primary,
        }}
      >
        {title}
      </Typography>
      {!isMarker && event.activityType && (
        <Typography
          sx={{
            mt: 0.5,
            fontSize: '13px',
            color: theme.palette.text.secondary,
          }}
        >
          Type · {event.activityType}
        </Typography>
      )}
      {editableStatus && (
        <Box sx={{ mt: 1.5 }}>
          <StatusChip
            activityId={editableStatus.activityId}
            status={editableStatus.value}
            onCommitted={onStatusCommitted}
          />
        </Box>
      )}
    </Box>
  )
}
